import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../../firebase";
import { createItem, DUPLICATE_ITEM } from "../../services/itemService";
import { checkHsn, ITEM_GST_OPTIONS } from "../../utils/gstBill";
import {
  collection, query, where, getDocs,
  updateDoc, doc, addDoc, serverTimestamp,
  getDoc
} from "firebase/firestore";

function RequestCard({ req, allItems, onDone }) {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const [name,        setName]        = useState(req.item_name);
  const [unit,        setUnit]        = useState(req.unit || "");
  const [category,    setCategory]    = useState("MATERIAL");
  const [hsnCode,     setHsnCode]     = useState("");
  const [gstPercent,  setGstPercent]  = useState("");
  const [itemType,    setItemType]    = useState(""); // "inventory" | "tool"
  const [loading,     setLoading]     = useState(false);

  const similar = allItems.filter(item => {
    const reqWords  = req.item_name.toLowerCase().split(" ").filter(w => w.length > 2);
    const itemWords = item.item_name.toLowerCase().split(" ");
    const matches   = reqWords.filter(w =>
      itemWords.some(iw => iw.includes(w) || w.includes(iw))
    );
    return matches.length >= 2;
  });

  const handleApprove = async () => {
    // ── GATE: item type must be selected first ──
    if (!itemType) {
      alert("Please select item type before approving.");
      return;
    }

    // ── Tool/Asset selected → reject + navigate to Asset Registry ──
    if (itemType === "tool") {
      const goToRegistry = window.confirm(
        `"${name}" will be rejected from Item Master.\n\nWould you like to go to Asset Registry now to add it properly?\n\nOK = Go to Asset Registry (name pre-filled)\nCancel = Just reject`
      );
      try {
        await updateDoc(doc(db, "item_requests", req.id), {
          status:        "rejected",
          reject_reason: "Tool/Asset item — to be added via Asset Registry.",
          rejected_at:   serverTimestamp(),
          rejected_by:   user.name || "",
          rejected_by_uid: user.uid || "",
          updated_at:    serverTimestamp(),
        });

        // ── Mark source MRF item as redirected_to_asset_registry ──
        if (req.source_mrf_id && req.item_index !== undefined && req.item_index !== null) {
          try {
            const mrfDocRef = doc(db, "material_requests", req.source_mrf_id);
            const mrfSnap   = await getDoc(mrfDocRef);
            if (mrfSnap.exists()) {
              const mrfItems    = [...mrfSnap.data().items];
              const targetIndex = Number(req.item_index);
              if (mrfItems[targetIndex]) {
                mrfItems[targetIndex] = {
                  ...mrfItems[targetIndex],
                  item_status:        "redirected_to_asset_registry",
                  asset_name:         name.trim(),
                  redirected_at:      new Date().toISOString(),
                  redirected_by:      user.name || "",
                };
                await updateDoc(mrfDocRef, { items: mrfItems, updated_at: serverTimestamp() });
              }
            }
          } catch (mrfErr) {
            console.warn("MRF item redirect failed (non-blocking):", mrfErr);
          }
        }

        // ── Notify SM/Owner ──
        try {
          const { createNotification } = await import("../../services/notificationService");
          await createNotification({
            type:               "tool_redirected_to_asset",
            notification_class: "informational",
            title:              `🔧 Tool item redirected — "${name.trim()}"`,
            body:               `"${name.trim()}" was requested in MRF but identified as a Tool/Asset. It has been added to Asset Registry. Please transfer it to the technician.`,
            for_roles:          ["owner", "sm", "manager"],
            company_id:         req.company_id,
            reference_id:       req.source_mrf_id || req.id,
            reference_type:     "material_request",
            route:              req.source_mrf_id ? `/store-mrf/${req.source_mrf_id}` : "/assets",
            is_read:            false,
            resolved:           false,
          });
        } catch (notifErr) {
          console.warn("Notification failed (non-blocking):", notifErr);
        }

        onDone();
        if (goToRegistry) {
          navigate("/assets", {
            state: {
              prefill_name:      name.trim(),
              from_item_request: true,
              item_request_id:   req.id,
              requested_by:      req.requested_by_name || "",
            }
          });
        } else {
          alert(`"${name}" rejected. Add it through Asset Registry when ready.`);
        }
      } catch (err) {
        alert("Error: " + err.message);
      }      return;
    }

    // ── Normal inventory approve flow ──
    if (!name.trim()) { alert("Item name is required."); return; }
    if (!unit.trim()) { alert("Unit is required."); return; }
    const hsnCheck = checkHsn(hsnCode);
    if (!hsnCheck.ok) { alert(hsnCheck.reason); return; }

    const exists = allItems.find(
      i => i.item_name_lower === name.toLowerCase().trim()
    );
    if (exists) {
      alert(`"${name}" already exists in Item Master. Use the existing item instead.`);
      return;
    }

    setLoading(true);
    try {
      // STEP 1-4 — P4-2: item + its stock doc in ONE batch (itemService):
      // platform-wide item_id, code = prefix + id number, name-key duplicate check.
      const { item_id, item_code } = await createItem({
        company_id:       req.company_id,
        item_name:        name,
        unit, category,
        hsn_code:         hsnCheck.hsn,
        gst_percent:      gstPercent,
        description:      req.description || "",
        source:           "item_request",
        original_request: req.item_name,
      });

      // STEP 5 — update item_request status
      await updateDoc(doc(db, "item_requests", req.id), {
        status:           "approved",
        approved_item_id: item_id,
        canonical_name:   name.trim(),
        approved_at:      serverTimestamp(),
        approved_by:      user.name || "",
        approved_by_uid:  user.uid || "",
        updated_at:       serverTimestamp(),
      });

      // STEP 6 — update source MRF item if exists
      if (req.source_mrf_id && req.item_index !== undefined && req.item_index !== null) {
        try {
          const mrfDocRef = doc(db, "material_requests", req.source_mrf_id);
          const mrfSnap   = await getDoc(mrfDocRef);
          if (mrfSnap.exists()) {
            const mrfData     = mrfSnap.data();
            const mrfItems    = Array.isArray(mrfData.items) ? [...mrfData.items] : [];
            const targetIndex = Number(req.item_index);
            if (mrfItems[targetIndex]) {
              mrfItems[targetIndex] = {
                ...mrfItems[targetIndex],
                item_id, item_code,
                item_name:              name.trim(),
                item_name_lower:        name.toLowerCase().trim(),
                category, unit,
                is_custom_item:         false,
                requires_master_review: false,
                inventory_linked:       true,
                procurement_type:       "purchase",
              };
              await updateDoc(mrfDocRef, { items: mrfItems, updated_at: serverTimestamp() });
            }
          }
        } catch (mrfUpdateErr) {
          console.error("MRF item update failed (non-blocking):", mrfUpdateErr);
        }
      }

      onDone();
      alert(`"${name}" added to Item Master. ID: ${item_id}`);
    } catch (err) {
      console.error(err);
      alert(err?.code === DUPLICATE_ITEM ? err.message : "Error approving. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleUseExisting = async (existingItem) => {
    await updateDoc(doc(db, "item_requests", req.id), {
      status:           "merged",
      merged_with:      existingItem.id,
      merged_item_name: existingItem.item_name,
      approved_at:      serverTimestamp(),
      approved_by:      user.name || "",
      approved_by_uid:  user.uid || "",
      updated_at:       serverTimestamp(),
    });
    onDone();
    alert(`Merged with existing item: "${existingItem.item_name}"`);
  };

  const handleReject = async () => {
    const reason = prompt("Reason for rejection:");
    if (!reason) return;
    await updateDoc(doc(db, "item_requests", req.id), {
      status:        "rejected",
      reject_reason: reason,
      rejected_at:   serverTimestamp(),
      rejected_by:   user.name || "",
      rejected_by_uid: user.uid || "",
      updated_at:    serverTimestamp(),
    });
    onDone();
  };

  return (
    <div className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm space-y-3">

      {/* Request info */}
      <div className="bg-slate-50 rounded-xl p-3">
        <p className="text-xs text-slate-400">
          Requested by: <span className="font-medium text-slate-600">{req.requested_by_name}</span>
        </p>
        <p className="text-sm font-semibold text-slate-700 mt-1">"{req.item_name}"</p>
        {req.description && (
          <p className="text-xs text-slate-400 mt-1">{req.description}</p>
        )}
      </div>

      {/* Similar items */}
      {similar.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3">
          <p className="text-xs font-semibold text-yellow-700 mb-2">
            ⚠️ Similar items already in master:
          </p>
          {similar.map(s => (
            <div key={s.id}
              className="flex justify-between items-center py-1.5 border-b border-yellow-100 last:border-0">
              <span className="text-xs text-slate-700">
                {s.item_name}
                <span className="text-slate-400 ml-1">({s.unit})</span>
              </span>
              <button
                onClick={() => handleUseExisting(s)}
                className="text-xs bg-yellow-500 text-white px-2 py-1 rounded-lg ml-2 shrink-0"
              >Use This</button>
            </div>
          ))}
        </div>
      )}

      {/* ── MANDATORY ITEM TYPE SELECTION ── */}
      <div>
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
          What type of item is this? <span className="text-red-500">*</span>
        </p>
        <div className="grid grid-cols-2 gap-2">

          {/* Option 1 — Inventory */}
          <button
            onClick={() => setItemType("inventory")}
            style={{
              border: itemType === "inventory" ? "2px solid #16A34A" : "2px solid #E5E7EB",
              background: itemType === "inventory" ? "#F0FDF4" : "#fff",
              borderRadius: 12, padding: "12px 10px", textAlign: "left",
              cursor: "pointer", transition: "all 0.15s",
            }}>
            <div style={{ fontSize: 18, marginBottom: 4 }}>📦</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>
              Material / Consumable
            </div>
            <div style={{ fontSize: 11, color: "#6B7280", marginTop: 3, lineHeight: 1.4 }}>
              Goes into stock. Issued via MRF.
            </div>
          </button>

          {/* Option 2 — Tool/Asset */}
          <button
            onClick={() => setItemType("tool")}
            style={{
              border: itemType === "tool" ? "2px solid #DC2626" : "2px solid #E5E7EB",
              background: itemType === "tool" ? "#FEF2F2" : "#fff",
              borderRadius: 12, padding: "12px 10px", textAlign: "left",
              cursor: "pointer", transition: "all 0.15s",
            }}>
            <div style={{ fontSize: 18, marginBottom: 4 }}>🔧</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>
              Tool / Asset
            </div>
            <div style={{ fontSize: 11, color: "#6B7280", marginTop: 3, lineHeight: 1.4 }}>
              Not in stock. Managed via Asset Registry.
            </div>
          </button>
        </div>

        {/* Tool selected — confirmation */}
        {itemType === "tool" && (
          <div style={{
            marginTop: 10, padding: "10px 12px",
            background: "#F0FDF4", border: "1px solid #BBF7D0",
            borderRadius: 10, fontSize: 12, color: "#166534", lineHeight: 1.5,
          }}>
            ✅ Correct — this is a Tool / Asset.<br />
            It will be tracked via <strong>Asset Registry</strong>, not issued from stock. Add it through <strong>Create Asset</strong> after confirming here.
          </div>
        )}
      </div>

      {/* Name — always editable regardless of item type */}
      <div className="space-y-2">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
          Correct item name if needed:
        </p>
        <input
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="Item name"
          className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm font-medium outline-none focus:ring-2 focus:ring-green-300"
        />
      </div>

      {/* Unit + Category — only for inventory */}
      {itemType === "inventory" && (
        <div className="grid grid-cols-2 gap-2">
          <input
            value={unit}
            onChange={e => setUnit(e.target.value)}
            placeholder="Unit"
            className="border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-green-300"
          />
          <select
            value={category}
            onChange={e => setCategory(e.target.value)}
            className="border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-green-300 bg-white"
          >
            <option value="MATERIAL">Material — Pipe / Wire / Duct / Equipment</option>
            <option value="CONSUMABLE">Consumable — Spare Part / Chemical / Gas</option>
          </select>
        </div>
      )}

      {/* HSN + GST % - both optional; blank GST % = not set (D-325). */}
      {itemType === "inventory" && (
        <div className="grid grid-cols-2 gap-2">
          <input
            value={hsnCode}
            inputMode="numeric"
            maxLength={8}
            onChange={e => setHsnCode(e.target.value.replace(/\s+/g, ""))}
            placeholder="HSN code (optional)"
            className="border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-green-300"
          />
          <select
            value={gstPercent}
            onChange={e => setGstPercent(e.target.value)}
            className="border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-green-300 bg-white"
          >
            <option value="">GST % (optional)</option>
            {ITEM_GST_OPTIONS.map(r => <option key={r} value={String(r)}>{r}%</option>)}
          </select>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2">
        <button
          onClick={handleReject}
          className="flex-1 border border-red-200 text-red-500 rounded-xl py-2.5 text-sm font-medium"
        >❌ Reject</button>
        <button
          onClick={handleApprove}
          disabled={loading || !itemType}
          style={{
            flex: 2,
            background: !itemType
              ? "#D1D5DB"
              : itemType === "tool"
              ? "#DC2626"
              : "#16A34A",
            color: "#fff",
            border: "none",
            borderRadius: 12,
            padding: "10px 0",
            fontSize: 13,
            fontWeight: 700,
            cursor: !itemType ? "not-allowed" : "pointer",
            opacity: loading ? 0.6 : 1,
          }}
        >
          {loading
            ? "Processing..."
            : !itemType
            ? "Select item type first"
            : itemType === "tool"
            ? "🔧 Confirm as Tool / Asset →"
            : "✅ Approve & Add to Master"}
        </button>
      </div>
    </div>
  );
}

export default function ItemRequests() {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const [requests, setRequests] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [reqSnap, itemSnap] = await Promise.all([
        getDocs(query(
          collection(db, "item_requests"),
          where("company_id", "==", user.company_id),
          where("status",     "==", "pending")
        )),
        getDocs(query(
          collection(db, "items"),
          where("company_id", "==", user.company_id)
        )),
      ]);
      setRequests(reqSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setAllItems(itemSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error("ItemRequests fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <p className="p-4 text-sm text-slate-400">Loading...</p>;

  return (
    <div className="p-4 max-w-2xl mx-auto pb-24">
      <div className="flex items-center gap-3 mb-1">
        <h1 className="text-xl font-bold text-slate-800">Item Requests</h1>
        {requests.length > 0 && (
          <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">
            {requests.length}
          </span>
        )}
      </div>
      <p className="text-xs text-slate-400 mb-5">
        Review each request carefully — select item type before approving.
      </p>

      {requests.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-4xl mb-3">✅</p>
          <p className="text-slate-400 text-sm">No pending item requests</p>
        </div>
      ) : (
        <div className="space-y-4">
          {requests.map(req => (
            <RequestCard
              key={req.id}
              req={req}
              allItems={allItems}
              onDone={fetchAll}
            />
          ))}
        </div>
      )}
    </div>
  );
}
